import type { SemaineAgenda } from '../partage/api/types';
import { majuscule } from '../partage/vocabulaire';
import { aujourdhui, heureDe, heureLisible, jourCourt, plageDeLaSemaine } from '../partage/datesEnMots';
import { teinteDuProjet } from '../apparence';

/**
 * La Chronologie de l'Agenda — design/agenda.html. La page d'aujourd'hui, gardée
 * telle quelle au ticket 15 de retours-testeurs (décision d'Igor) : colonnes par semaine,
 * cartes, bandeau. Une chose de plus : une journée préparée se montre dans sa semaine, en
 * pointillé (« Ta journée prévue »), et s'ouvre dans le Calendrier.
 *
 * **Son sujet n'est pas le calendrier, ce sont les COLLISIONS.** L'entretien du candidat
 * mardi, la visite médicale du nouveau jeudi, et la livraison des parasols vendredi : trois
 * projets différents, une seule semaine, une seule personne. Ni l'arbre ni la carte ne
 * peuvent le montrer — ils rangent par thème, pas par temps.
 *
 * **Trois natures, trois formes** : un rendez-vous est plein (il a une heure et une durée,
 * donc il peut partir en `.ics`), une échéance est un contour à la couleur de son dossier,
 * une réponse attendue est en pointillé ambré.
 *
 * **Rien n'est encadré.** Une semaine n'est pas une case : c'est une colonne posée sous un
 * filet de tête, et le filet est tout ce qui la distingue — encre pour celle où l'on est,
 * ambre pour celle qui percute. Mettre chaque semaine dans une carte bordée découpe le
 * temps en six boîtes égales, exactement ce que cette vue existe pour défaire.
 *
 * **L'app ne notifie pas, elle délègue.** Le rappel est le fait de l'agenda qu'on regarde
 * déjà : c'est pour ça qu'un rendez-vous n'est pas raté au milieu d'un service.
 *
 * Depuis le chantier `revue-design` (ticket 06, maquette `design/agenda.html`), le bandeau du
 * bas est parti : « Envoyer dans mon agenda » est en haut de l'Agenda, qui tient la sélection
 * (un clic sur une carte la choisit, l'anneau le montre).
 */
export function Chronologie({
  semaines,
  choisis,
  onBasculer,
  onOuvrir,
  onOuvrirLaJournee,
}: {
  semaines: SemaineAgenda[];
  /** Les cartes choisies pour l'export (`cleDe`). */
  choisis: Set<string>;
  onBasculer: (cle: string) => void;
  onOuvrir: (bulleId: string) => void;
  /** Une journée préparée s'ouvre dans le Calendrier, sur son jour. */
  onOuvrirLaJournee: (jour: string) => void;
}) {
  const laPlusChargee = semaines.reduce((max, s) => Math.max(max, s.evenements.length), 0);
  // La semaine qui percute : la plus haute pile, et seulement si elle porte plus d'une chose.
  const quiPercute =
    laPlusChargee > 1 ? semaines.findIndex((s) => s.evenements.length === laPlusChargee) : -1;

  return (
    <div className="agenda-semaines" data-tuto="agenda-semaines">
      {semaines.map((semaine, i) => (
        <Semaine
          key={semaine.debut}
          semaine={semaine}
          enCours={i === 0}
          percute={i === quiPercute}
          choisis={choisis}
          onBasculer={onBasculer}
          onOuvrir={onOuvrir}
          onOuvrirLaJournee={onOuvrirLaJournee}
        />
      ))}
    </div>
  );
}

/** La clé d'un événement porte sa nature : une bulle peut en produire trois. */
export function cleDe(e: { bulleId: string; nature: string }): string {
  return `${e.bulleId}|${e.nature}`;
}
export function bulleDe(cle: string): string {
  return cle.split('|')[0] as string;
}

/** Les dossiers qui posent quelque chose de daté, dans l'ordre où le temps les amène. */
export function dossiersDe(semaines: SemaineAgenda[]) {
  const vus = new Map<string, { id: string; titre: string; couleur: string | null }>();
  for (const s of semaines) {
    for (const e of s.evenements) {
      if (e.dossier && !vus.has(e.dossier.id)) vus.set(e.dossier.id, e.dossier);
    }
  }
  return [...vus.values()];
}

function Semaine({
  semaine,
  enCours,
  percute,
  choisis,
  onBasculer,
  onOuvrir,
  onOuvrirLaJournee,
}: {
  semaine: SemaineAgenda;
  enCours: boolean;
  percute: boolean;
  choisis: Set<string>;
  onBasculer: (cle: string) => void;
  onOuvrir: (id: string) => void;
  onOuvrirLaJournee: (jour: string) => void;
}) {
  const combienDeDossiers = new Set(semaine.evenements.map((e) => e.dossier?.id ?? '—')).size;
  const journees = semaine.journees ?? [];

  return (
    <section className={`semaine${percute ? ' semaine--percute' : enCours ? ' semaine--en-cours' : ''}`}>
      <header>
        {/* Une semaine est une PLAGE : n'afficher que son lundi obligerait à calculer de tête où elle s'arrête. */}
        <h2>{plageDeLaSemaine(semaine.debut, semaine.fin)}</h2>
        {/*
          Tout calcul a deux rendus : la pile la plus haute se voit, et se dit aussi en mots
          (SPEC §5.2). Ambre, jamais rouge — une semaine chargée n'est pas une faute.
        */}
        {percute ? (
          <span className="semaine-mot semaine-mot--monde">
            {enCours && 'Cette semaine · '}
            {semaine.collisions.length > 0
              ? `${semaine.evenements.length} choses, dont deux qui se chevauchent`
              : `${semaine.evenements.length} choses, ${combienDeDossiers} ${
                  combienDeDossiers > 1 ? 'dossiers' : 'dossier'
                }`}
          </span>
        ) : (
          enCours && <span className="semaine-mot semaine-mot--toi">Cette semaine</span>
        )}
      </header>

      {semaine.evenements.length === 0 && journees.length === 0 ? (
        <p className="semaine-vide">Rien de daté</p>
      ) : (
        <ul>
          {semaine.evenements.map((e) => {
            const cle = cleDe(e);
            return (
              <li key={cle}>
                <label
                  className={`evenement evenement--${e.nature}${
                    choisis.has(cle) ? ' evenement--choisi' : ''
                  }`}
                  style={{ ['--teinte' as string]: teinteDuProjet(e.dossier?.couleur) }}
                >
                  {/*
                    La case reste dans la page — l'écran se pilote au clavier (SPEC §5.3) —
                    mais elle ne se dessine pas : ce qui est choisi se voit à l'anneau, et
                    une colonne de cases à cocher ferait du temps un formulaire.
                  */}
                  <input
                    type="checkbox"
                    className="evenement-case"
                    checked={choisis.has(cle)}
                    onChange={() => onBasculer(cle)}
                    aria-label={`choisir ${e.titre} pour l’export`}
                  />
                  <span className="evenement-corps">
                    <span className="evenement-titre">{e.titre}</span>
                    <span className="evenement-detail">
                      {e.nature !== 'rendez_vous' && <span className="evenement-puce" aria-hidden="true" />}
                      {quand(e)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="evenement-ouvrir"
                    onClick={(ev) => {
                      ev.preventDefault();
                      onOuvrir(e.bulleId);
                    }}
                    aria-label={`ouvrir ${e.titre}`}
                  >
                    <Chevron />
                  </button>
                </label>
              </li>
            );
          })}
          {/*
            Ta journée prévue : à toi, pas au monde. En pointillé bleu-vert, jamais pleine
            comme un rendez-vous ; elle ne part pas dans l'export, elle s'ouvre dans le Calendrier.
          */}
          {journees.map((j) => (
            <li key={`journee-${j.jour}`}>
              <button type="button" className="evenement evenement--journee" onClick={() => onOuvrirLaJournee(j.jour)}>
                <span className="evenement-corps">
                  <span className="evenement-titre">Ta journée préparée</span>
                  <span className="evenement-detail">
                    {majuscule(jourCourt(j.jour))} · {j.combien} {j.combien > 1 ? 'choses' : 'chose'}, à partir de{' '}
                    {heureLisible(j.debut)}
                  </span>
                </span>
                <span className="evenement-ouvrir" aria-hidden="true">
                  <Chevron />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Chevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M5.6 2.6L11 8l-5.4 5.4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function quand(e: SemaineAgenda['evenements'][number]): string {
  const jour = majuscule(jourCourt(e.jour));

  if (e.nature === 'rendez_vous' && e.debut) {
    const instant = new Date(e.debut);
    const debut = heureDe(instant);
    if (!e.dureeMin) return `${jour} · ${debut}`;
    const fin = heureDe(new Date(instant.getTime() + e.dureeMin * 60_000));
    return `${jour} · ${debut} — ${fin}`;
  }

  // Une attente dépassée se dit sans reproche : elle attend encore, elle n'est pas en faute.
  if (e.nature === 'attente') {
    return `${jour} · ${e.jour < aujourdhui() ? 'attendue depuis' : 'attendue'}`;
  }
  return jour;
}
